import ForbiddenCircle from '@betfan/icons/linear/EssentionalUI/ForbiddenCircle.svg?react';
import cn from 'classnames';
import { formatCouponPrice } from 'features/coupon/utility';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { BONUS_BALANCE, FREEBET, MAIN_BALANCE } from 'shared/constants/common';
import DotIndicator from './DotIndicator';

interface CouponRadioButtonProps {
	name: string;
	id: string;
	value: string;
	dotNumber?: any;
	onChange: (e: any) => void;
	checked: boolean;
	disabled?: boolean;
	cyTestRef?: string;
	mainBalance: number | null;
	depositBalanceBelowTwo: boolean;
}

export const CouponRadioButton: React.FC<CouponRadioButtonProps> = ({
	checked,
	name,
	id,
	value,
	onChange,
	disabled,
	cyTestRef,
	mainBalance,
	depositBalanceBelowTwo,
	dotNumber,
}) => {
	const { t } = useTranslation();

	return (
		<div
			className={CouponRadioButtonStyles.button(checked, depositBalanceBelowTwo, value)}
			onClick={(e) => {
				if (mainBalance === 0 && (value === BONUS_BALANCE || value === FREEBET)) {
					return;
				}
				onChange(e);
			}}
			role="radio"
			aria-checked={checked}
			data-cy={cyTestRef}
		>
			{dotNumber != undefined && dotNumber > 0 && <DotIndicator dotNumber={dotNumber} className="absolute -top-3 -right-2" />}
			<input type="radio" disabled={disabled} checked={checked} name={name} id={id} className="hidden" />
			<label className="w-full h-full flex flex-col justify-center items-center cursor-pointer" htmlFor={id}>
				<div className={CouponRadioButtonStyles.valueContainer(checked, depositBalanceBelowTwo, value, mainBalance)}>
					<div className="flex items-center">
						{depositBalanceBelowTwo && value === MAIN_BALANCE && (
							<ForbiddenCircle className="text-error-600 dark:text-error-400 w-3 h-3" />
						)}
					</div>
					<span className="flex items-center">{t(value)}</span>
				</div>
				<div className={CouponRadioButtonStyles.labelContainer(checked, depositBalanceBelowTwo, value, mainBalance)}>
					<span>
						{mainBalance ? formatCouponPrice(mainBalance) : '0'} {t('currency')}
					</span>
				</div>
			</label>
		</div>
	);
};

const CouponRadioButtonStyles = {
	button: (checked: boolean, depositBalanceBelowTwo: boolean, value: string) => {
		let colors;
		if (depositBalanceBelowTwo && value === MAIN_BALANCE) {
			colors = 'bg-error-200 dark:bg-error-700';
		} else {
			if (checked) {
				colors = 'bg-primary-500';
			} else {
				colors = 'bg-gray-150 dark:bg-dark-400';
			}
		}

		return cn(
			'border-0 flex items-center justify-center flex-col flex-grow min-h-[94px] h-[94px] rounded-4 w-full cursor-pointer relative overflow-ellipsis',
			colors,
		);
	},
	valueContainer: (checked: boolean, depositBalanceBelowTwo: boolean, value: string, mainBalance: number | null) => {
		return cn(
			'text-[12px] uppercase leading-[28px] flex flex-row items-center gap-1 mb-1 h-5',
			CouponRadioButtonStyles._colors(checked, depositBalanceBelowTwo, value, mainBalance),
		);
	},
	labelContainer: (checked: boolean, depositBalanceBelowTwo: boolean, value: string, mainBalance: number | null) => {
		return cn(
			'font-normal h-5 whitespace-nowrap overflow-ellipsis justify-center flex w-full items-end',
			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			Number(mainBalance).toString().split('.')[0]!.length > 4 ? 'text-[14px] pb-[1px]' : 'text-[18px]',
			CouponRadioButtonStyles._colors(checked, depositBalanceBelowTwo, value, mainBalance),
		);
	},

	_colors: (checked: boolean, depositBalanceBelowTwo: boolean, value: string, mainBalance: number | null) => {
		if (depositBalanceBelowTwo && value === MAIN_BALANCE) {
			return 'text-error-600 dark:text-error-400';
		}
		if (checked) {
			return 'text-gray-100';
		}

		if (mainBalance === 0 && (value === BONUS_BALANCE || value === FREEBET)) {
			return 'text-primary-300 dark:text-dark-500';
		}

		return 'text-primary-500 dark:text-gray-100';
	},
};
